import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Shared by the invite link and the forgot-password link.
export default function SetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [check, setCheck] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get(`/auth/token-check?token=${encodeURIComponent(token)}`)
      .then(({ data }) => setCheck(data))
      .catch(() => setCheck({ valid: false }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("The two passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/set-password", { token, password });
      toast.success("Password set — please sign in");
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F7F5F1] px-6">
      <div className="fade-up w-full max-w-sm bg-white p-8 shadow-sm" data-testid="set-password-page">
        <div className="gold-rule w-14" />
        <h1 className="mt-5 font-invoice text-3xl">
          {check?.kind === "invite" ? "Welcome aboard" : "New password"}
        </h1>

        {check === null && (
          <p className="mt-3 text-sm text-muted-foreground">Checking your link…</p>
        )}

        {check?.valid === false && (
          <div className="mt-4 border-l-2 border-red-600 bg-red-50 p-3 text-sm text-red-700"
               data-testid="token-invalid">
            This link has expired or has already been used. Ask the owner for a new one,
            or use “Forgot password?” on the sign-in screen.
          </div>
        )}

        {check?.valid && (
          <form onSubmit={submit} className="mt-5 space-y-4">
            <p className="text-sm text-muted-foreground">
              {check.name ? `${check.name} — ` : ""}choose a password for {check.email}.
              Nobody else can see it.
            </p>
            <div>
              <Label className="text-xs uppercase tracking-wider">New password</Label>
              <Input
                type="password"
                className="mt-1 h-11 rounded-none"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="new-password-input"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                At least 10 characters, with a letter and a number.
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Type it again</Label>
              <Input
                type="password"
                className="mt-1 h-11 rounded-none"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                data-testid="confirm-password-input"
              />
            </div>
            {error && (
              <p className="text-sm font-semibold text-red-700" data-testid="set-password-error">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={busy}
              className="h-11 w-full rounded-none bg-[#C5A059] font-semibold text-[#1b1b1b] hover:bg-[#B08F4A]"
              data-testid="save-password-button"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
              Save password
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
